import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FixedSizeList } from 'react-window';
import { AiOutlineSearch } from 'react-icons/ai';
import { useSymbolContext } from '../../../contexts/Symbol-Context.js';
import { useTranslation } from 'react-i18next';
import './React-Select.scss';

const valid = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
function prices(quote = {}, details = {}) {
  const digits = Math.min(8, Math.max(0, valid(details.digit) ? Number(details.digit) : 5));
  const price = value => valid(value) ? Number(value).toLocaleString('en-US', { useGrouping: false, minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits }) : '—';
  const spread = valid(quote.ask) && valid(quote.bid) && valid(details.pip_position)
    ? ((Number(price(quote.ask)) - Number(price(quote.bid))) * Math.pow(10, Number(details.pip_position))).toFixed(2) : '—';
  return [price(quote.bid), price(quote.ask), spread];
}
function SymbolRow({ index, style, data }) {
  const name = data.names[index];

  return <button type="button" style={style} className={'instrument-row' + (name === data.selected ? ' is-selected' : '')}
    tabIndex={-1}
    onMouseLeave={(event) => { if (event.currentTarget === document.activeElement) event.currentTarget.blur(); }}
    onClick={() => data.choose(name)}>
    <strong>{name}</strong>
  </button>;
}
export default function ReactSelect() {
  const { t } = useTranslation();
  const { symbolNames, symbolData, symbolDetails, selectedSymbolOption, handleSymbolOptionChange } = useSymbolContext();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [present, setPresent] = useState(false);
  useEffect(() => {
    if (open) { setPresent(true); return; }
    const timer = setTimeout(() => setPresent(false), 180);
    return () => clearTimeout(timer);
  }, [open]);
  const container = useRef(null), trigger = useRef(null);
  const names = useMemo(() => symbolNames.filter(name => name.toLowerCase().includes(query.trim().toLowerCase())), [symbolNames, query]);
  const selected = selectedSymbolOption?.value;
  const labels = [t('symbol.bid'), t('symbol.ask'), t('orderPanel.spread')];
  const values = prices(symbolData[selected], symbolDetails[selected]);
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!container.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const choose = name => { handleSymbolOptionChange({ value: name, label: name }); setOpen(false); setQuery(''); trigger.current?.focus(); };
  return <div className="instrument-picker" ref={container} onKeyDown={event => {
    if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
  }}>
    <button type="button" ref={trigger} className={"instrument-trigger" + (!selected ? " is-empty" : "")} aria-expanded={open} aria-controls="instrument-menu"
      onClick={() => { setOpen(value => !value); setQuery(''); }}>
      <strong className="instrument-name">{selected || t('settings.selectSymbol')}</strong>
      {selected && labels.map((label, i) => <span className={'instrument-inline-quote quote-' + i} key={label} title={label} aria-label={label + ": " + values[i]}><strong>{values[i]}</strong></span>)}
      <svg className="instrument-chevron" viewBox="0 0 20 20" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }}><path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" /></svg>
    </button>
    {(open || present) && <div className="instrument-menu" data-open={open} aria-hidden={!open} inert={!open ? "" : undefined} id="instrument-menu">
      <div className="instrument-search"><AiOutlineSearch /><input autoFocus aria-label={t('settings.selectSymbol')} placeholder={t('settings.selectSymbol')} value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => {
        if (event.key === 'Enter' && names.length) choose(names[0]);
        if (event.key === 'ArrowDown') { event.preventDefault(); container.current.querySelector('.instrument-row')?.focus(); }
      }} /></div>
      {names.length ? <FixedSizeList className="instrument-list" height={Math.min(280, names.length * 28)} width="100%" itemSize={28} itemCount={names.length} overscanCount={3}
        itemKey={(index, data) => data.names[index]} itemData={{ names, selected, choose }}>{SymbolRow}</FixedSizeList>
        : <p className="instrument-empty">{t('common.noResultsFound')}</p>}
    </div>}
  </div>;
}
